import * as React from "react";
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import { POLL_INTERVAL_MS, POLL_TIMEOUT_MS } from "@/hooks/queries/scoring-types";
import {
  isTerminalCaseStatus,
  type CaseSubmissionStatusData,
} from "@/hooks/queries/calibration-submit-types";

// Calibration case-submission poll (Story 8.10, AC4). Modelled on
// `useScoringJobStatus` — the repo's reference `refetchInterval` hook — and reusing
// its cadence constants so the two polls can't drift: 2 s while non-terminal, stop
// on terminal, 5-minute overall deadline, retry: false.
//
// The terminal predicate is calibration's own (`isTerminalCaseStatus`): the scoring
// enum has no "scored" value, so `isTerminalStatus` there would never stop this loop.

export const CASE_SUBMISSION_STATUS_QUERY_KEY = "calibration-case-submission-status";

export interface UseCaseSubmissionStatusResult {
  status: CaseSubmissionStatusData["status"] | undefined;
  isError: boolean;
  /**
   * CONSECUTIVE failed polls (0 while healthy, resets on any success or a fresh
   * submit) — drives the scoring screen's inline connection note. Mirrors
   * `useScoringJobStatus`: NOT TanStack's `failureCount`, which counts retries
   * within ONE fetch cycle and resets when the next interval fetch starts (with
   * `retry: false` it can never exceed 1 across polls).
   */
  failureCount: number;
  /** The 5-minute deadline elapsed before a terminal status arrived (AC4). */
  timedOut: boolean;
}

/**
 * Poll one case's submission until it is `scored` or `failed`.
 *
 * @param caseId  The case being polled.
 * @param enabled Poll only after a submit has been accepted — mounting this while
 *   the clinician is still scoring would poll a case with no submission.
 * @param submissionNonce A value the caller bumps on every fresh submit attempt.
 *   It resets the deadline + `timedOut` latch so a RETRY after a `failed`/timeout
 *   outcome polls again. Without it the reset only fires when the polled target
 *   changes, but a same-case retry keeps `caseId`/`enabled` constant — so the
 *   5-minute deadline and, worse, the LATCHED `timedOutRef` would carry over and
 *   `refetchInterval` would bail immediately, leaving the screen stuck on the
 *   stale outcome even though a new submission is genuinely in flight (F1).
 */
export function useCaseSubmissionStatus(
  caseId: string | undefined,
  enabled: boolean,
  submissionNonce = 0,
): UseCaseSubmissionStatusResult {
  // Deadline held in BOTH a ref and state, for the reason `useScoringJobStatus`
  // documents: the `refetchInterval` callback is created once and reads the ref
  // synchronously (it cannot close over a later `timedOut`), while the state drives
  // the re-render. Reset together whenever the polled target OR the submission
  // attempt changes.
  const [timedOut, setTimedOut] = React.useState(false);
  const timedOutRef = React.useRef(false);
  const active = Boolean(caseId) && enabled;

  React.useEffect(() => {
    setTimedOut(false);
    timedOutRef.current = false;
    if (!active) return;
    const timer = setTimeout(() => {
      timedOutRef.current = true;
      setTimedOut(true);
    }, POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [caseId, active, submissionNonce]);

  const query = useQuery({
    queryKey: [CASE_SUBMISSION_STATUS_QUERY_KEY, caseId],
    queryFn: () =>
      apiFetch<CaseSubmissionStatusData>(
        `/calibration/cases/${encodeURIComponent(caseId ?? "")}/status`,
      ),
    enabled: active,
    refetchInterval: (query) => {
      if (isTerminalCaseStatus(query.state.data?.status)) return false;
      if (timedOutRef.current) return false;
      return POLL_INTERVAL_MS;
    },
    // A poll is a fresh snapshot each interval — the next interval re-polls, so a
    // transient failure must not retry-storm an endpoint behind an audit write.
    retry: false,
  });

  // Consecutive-failure derivation (mirrors `useScoringJobStatus`): `errorUpdateCount`
  // is the LIFETIME number of errored settles; snapshotting it at every success — and
  // resetting on a new target OR a fresh submit attempt — makes the difference the
  // consecutive-failure count. Ref writes are idempotent, so this is StrictMode-safe.
  const errorsAtLastSuccessRef = React.useRef(0);
  React.useEffect(() => {
    errorsAtLastSuccessRef.current = 0;
  }, [caseId, submissionNonce]);
  React.useEffect(() => {
    if (query.isSuccess) errorsAtLastSuccessRef.current = query.errorUpdateCount;
  }, [query.isSuccess, query.dataUpdatedAt, query.errorUpdateCount]);
  const failureCount = query.isError
    ? query.errorUpdateCount - errorsAtLastSuccessRef.current
    : 0;

  return { status: query.data?.status, isError: query.isError, failureCount, timedOut };
}
